import { createError } from '@common/neo.tax';
import { Button, colors, ModalActions, ModalHeader, Typography } from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-ring.svg';

import { CombineContributorInput, evictCache, useCurrentCompanyId } from '@neo.tax/shared';

import { StyledModal } from './combine-contributor-modal.styled';
import { useCombineContributor } from './hooks';

type CombineContributorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  combineContributorInput: CombineContributorInput | undefined;
};

export const CombineContributorModal = ({
  combineContributorInput,
  isOpen,
  onClose,
}: CombineContributorModalProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  const [combineContributor, { loading }] = useCombineContributor();
  const handleCancel = (): void => {
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!combineContributorInput) {
      throw createError('CombineContributorModalError', 'CombineContributorInput is undefined');
    }
    await combineContributor({
      variables: {
        companyId,
        input: combineContributorInput,
      },
      awaitRefetchQueries: true,
      refetchQueries: ['GetAscWages', 'GetAscWageSummary'],
      update: (cache) => {
        evictCache(cache, ['getPayrollExpense']);
      },
    });
    onClose();
  };

  return (
    <StyledModal isOpen={isOpen}>
      <ModalHeader
        icon={<AlarmBellRing width={24} color={colors.blue[600]} />}
        iconBgColor={colors.blue[50]}
        title={
          <Typography fontSize="base" fontWeight="semibold">
            Merge Expenses?
          </Typography>
        }
        copy="This will combine the allocation for this person across this and future periods. Use this when you want to link a person from payroll to ticketing data who wasn't automatically linked, or combine duplicate ticketing users."
      />
      <ModalActions hasTopBorder={false}>
        <Button disabled={loading} variant="white" rounded="regular" size="base" onClick={handleCancel}>
          Cancel
        </Button>
        <Button disabled={loading} variant="primary" rounded="regular" size="base" onClick={handleConfirm}>
          Save
        </Button>
      </ModalActions>
    </StyledModal>
  );
};
